import React, { useRef, useEffect, useState } from 'react';
import { ChevronDown } from 'lucide-react';

interface BentoCardProps {
  children: React.ReactNode;
  header?: React.ReactNode;
  footer?: React.ReactNode;
  expandable?: boolean;
  expanded?: boolean;
  onToggle?: () => void;
  className?: string;
  size?: 'sm' | 'md' | 'lg' | 'full';
  glow?: boolean;
  active?: boolean;
}

export default function BentoCard({
  children,
  header,
  footer,
  expandable = false,
  expanded = false,
  onToggle,
  className = '',
  size = 'md',
  glow = false,
  active = false,
}: BentoCardProps) {
  const innerRef = useRef<HTMLDivElement>(null);
  const [measuredHeight, setMeasuredHeight] = useState(0);

  useEffect(() => {
    if (innerRef.current) {
      setMeasuredHeight(innerRef.current.scrollHeight);
    }
  }, [children]);

  // Fix: use a ref for style injection to avoid TS issues with CSS custom props
  const expandRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (expandRef.current) {
      expandRef.current.style.setProperty('--expand-height', `${measuredHeight}px`);
    }
  }, [measuredHeight]);

  const sizeClass =
    size === 'full'
      ? 'col-span-full'
      : size === 'lg'
        ? 'col-span-1 md:col-span-2 lg:col-span-2'
        : size === 'sm'
          ? 'col-span-1'
          : 'col-span-1';

  return (
    <div
      className={`bento-card ${sizeClass} ${expanded ? 'bento-card--expanded' : ''} ${glow ? 'bento-card--glow' : ''} ${active ? 'bento-card--active' : ''} ${className}`}
    >
      {/* Header */}
      {header && (
        <div
          className={`bento-card__header ${expandable ? 'cursor-pointer' : ''}`}
          onClick={expandable ? onToggle : undefined}
          role={expandable ? 'button' : undefined}
          aria-expanded={expandable ? expanded : undefined}
        >
          <div className="flex-1 min-w-0">{header}</div>
          {expandable && (
            <div
              className={`bento-card__chevron shrink-0 ml-2 transition-transform duration-500 ${expanded ? 'rotate-180' : ''}`}
            >
              <ChevronDown size={14} style={{ color: 'var(--text-muted)' }} />
            </div>
          )}
        </div>
      )}

      {/* Compact content (always visible) */}
      <div className="bento-card__compact">{children}</div>

      {/* Expandable detail panel */}
      {expandable && (
        <div ref={expandRef} className="bento-card__expand">
          <div ref={innerRef} className="bento-card__expand-inner">
            {expanded && footer}
          </div>
        </div>
      )}

      {/* Non-expandable footer */}
      {!expandable && footer && (
        <div className="bento-card__footer">{footer}</div>
      )}
    </div>
  );
}